<script setup>
import { computed, onMounted, reactive, ref, watch, getCurrentInstance } from 'vue'
import { router } from '@inertiajs/vue3';
import { watchDebounced } from '@vueuse/core';
import MainLayout from '@/layout/main/MainLayout.vue';
import { useLoading } from '@/composables/useLoading';
import { useEnhancedLoading } from '@/composables/useEnhancedLoading';
import { useOrderStatus } from '@/composables/useOrderStatus.js';
import WiseTransferInstructions from '@/components/payment/WiseTransferInstructions.vue';
import PixInstructions from '@/components/payment/PixInstructions.vue';
import BoletoInstructions from '@/components/payment/BoletoInstructions.vue';
import Dialog from 'primevue/dialog';

defineOptions({
    layout: MainLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showLoading, hideLoading } = useLoading();
const { showPageLoading, hidePageLoading } = useEnhancedLoading('orders-table');
const { getStatusLabel: _getStatusLabel, getStatusSeverity, getCancellationTooltip } = useOrderStatus();

const getStatusLabel = (status, $t) => {
    if (status === 'AWAITING_CLIENT_APPROVAL') return $t('Awaiting approval');
    if (status === 'SUBMITTED') return $t('Awaiting Publication');
    return _getStatusLabel(status, $t);
};

const formatDate = (date) => {
    if (!date) return '-';
    if (typeof date === 'string') {
        if (date.includes('T') || date.includes(' ')) {
            return new Date(date).toLocaleDateString('pt-BR');
        }
        const [year, month, day] = date.split('-');
        return new Date(year, month - 1, day).toLocaleDateString('pt-BR');
    }
    return new Date(date).toLocaleDateString('pt-BR');
};

const coinStore = reactive({
    coin: 'USD',
    ratios: {
        'BRL': 1,
        'EUR': 1,
        'USD': 1,
        'GBP': 1
    }
})

const props = defineProps({
    title: String,
    coins: Object,
    filters: Object,
    mainStatuses: Object,
    statusCounts: Object,
    totalCount: Number,
    favorites: Array,
    interests: Array,
    languages: Array,
    countries: Array,
    sites: Array,
    orders: Object,
    wiseBankDetails: {
        type: Object,
        default: null,
    },
});

const showWiseModal = ref(false);
const selectedWiseOrder = ref(null);

const openWiseModal = (order) => {
    selectedWiseOrder.value = order;
    showWiseModal.value = true;
};

const showMpModal = ref(false);
const selectedMpOrder = ref(null);

const openMpModal = (order) => {
    selectedMpOrder.value = order;
    showMpModal.value = true;
};

const links = computed(() => {
    const _links = props.orders.links
    _links.shift()
    _links.pop()
    return _links
})

const sort = ref(props.filters.sort)

const filters = reactive({
    search: props.filters.search || '',
    price_from: props.filters.price_from || '',
    price_to: props.filters.price_to || '',
    language_id: props.filters.language_id || [],
    status: props.filters.status,
})

watch(sort, (n, o) => {
    showPageLoading();
    get();
});

watchDebounced(() => ({ ...filters }), (n, o) => {
    get()
}, {
    deep: true,
    debounce: 400
})

const get = async () => {
    try {
        showPageLoading();
        router.get(proxy.route ? proxy.route('client.orders.index') : '/client/orders', {
            sort: sort.value,
            filter: {
                status: filters.status,
                search: filters.search,
                price_from: filters.price_from,
                price_to: filters.price_to,
                language_id: Array.isArray(filters.language_id) && filters.language_id.length > 0 ? filters.language_id : [],
            },
        }, {
            preserveState: true,
            preserveScroll: true,
            only: ['orders', 'filters'],
            onStart: () => {
                showLoading($t('Loading orders...'));
            },
            onFinish: () => {
                hideLoading();
                hidePageLoading();
            },
            onError: () => {
                hideLoading();
                hidePageLoading();
            }
        })
    } catch (error) {
        console.error('Error in get function:', error);
        hideLoading();
        hidePageLoading();
    }
}

const filterByStatus = (status) => {
    if (filters.status === status) {
        filters.status = null;
    } else {
        filters.status = status;
    }
};

const clearStatusFilter = () => {
    filters.status = null;
};

const hasActiveFilters = () => {
    return filters.search ||
           filters.price_from ||
           filters.price_to ||
           filters.status;
};

const coinFormat = computed(() => props.coins[coinStore.coin])



onMounted(() => {
    setTimeout(() => {
        if (window.tippy) {
            window.tippy('[data-tippy-content]');
        }
    }, 500)
})
</script>

<template>

    <div class="grid px-2 sm:px-4 lg:px-0 w-full max-w-full">
   <!-- Filters Section -->
        <div class="flex w-full max-w-full gap-4 xl:gap-6 mb-6">
            <Card class="w-full">
                <template #header>
                    <div class="flex items-center gap-3 p-4 pb-0">
                        <i class="pi pi-search text-xl sm:text-2xl text-surface-500" />
                        <div>
                            <h2 class="text-lg sm:text-xl font-bold">{{ $t('Search orders') }}</h2>
                            <span class="text-xs sm:text-sm text-surface-500">{{ $t('Choose filters to start') }}</span>
                        </div>
                    </div>
                </template>

                <template #content>
                    <div>
                        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                            <div
                                @click="clearStatusFilter"
                                :class="[
                                    'rounded-lg p-4 cursor-pointer transition-all',
                                    !props.filters.status
                                        ? 'bg-blue-100 border-2 border-blue-500 shadow-md'
                                        : 'bg-white border border-surface-300 hover:border-blue-400'
                                ]"
                            >
                                <div :class="[
                                    'text-2xl font-bold mb-1',
                                    !props.filters.status ? 'text-blue-700' : 'text-surface-700'
                                ]">{{ totalCount || 0 }}</div>
                                <div :class="[
                                    'text-sm',
                                    !props.filters.status ? 'text-blue-600' : 'text-surface-600'
                                ]">{{ $t('View All') }}</div>
                            </div>
                            <div
                                v-for="(label, status) in mainStatuses"
                                :key="status"
                                @click="filterByStatus(status)"
                                :class="[
                                    'rounded-lg p-4 cursor-pointer transition-all',
                                    props.filters.status === status
                                        ? 'bg-blue-100 border-2 border-blue-500 shadow-md'
                                        : 'bg-white border border-surface-300 hover:border-blue-400'
                                ]"
                            >
                                <div :class="[
                                    'text-2xl font-bold mb-1',
                                    props.filters.status === status ? 'text-blue-700' : 'text-surface-700'
                                ]">{{ statusCounts?.[status] || 0 }}</div>
                                <div :class="[
                                    'text-sm',
                                    props.filters.status === status ? 'text-blue-600' : 'text-surface-600'
                                ]">{{ getStatusLabel(status, $t) }}</div>
                            </div>
                        </div>

                        <div class="flex flex-col lg:flex-row gap-4 items-end">
                        <!-- <div class="flex flex-col sm:flex-row gap-4 w-full lg:w-1/2">
                            Price range
                            <div class="w-full">
                                <label class="block text-sm font-medium text-surface-700 mb-2">
                                    <span class="inline-block w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                    {{ $t('Price range') }}
                                </label>
                                <div class="flex flex-col sm:flex-row gap-4">
                                    Price From
                                    <div class="w-full sm:w-1/2">
                                        <InputNumber
                                            v-model="filters.price_from"
                                            :placeholder="$t('From')"
                                            class="w-full h-[38px]"
                                            inputClass="!h-[38px]"
                                        />
                                    </div>
                                    Price To
                                    <div class="w-full sm:w-1/2">
                                        <InputNumber
                                            v-model="filters.price_to"
                                            :placeholder="$t('To')"
                                            class="w-full h-[38px]"
                                            inputClass="!h-[38px]"
                                        />
                                    </div>
                                </div>
                            </div>
                         </div> -->

                        <!-- Search by name -->
                        <div class="w-full lg:w-1/2">
                            <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('By name:') }}</label>
                            <IconField class="w-full">
                                <InputIcon class="pi pi-search" />
                                <InputText
                                    v-model="filters.search"
                                    :placeholder="$t('Search')"
                                    class="w-full h-[38px]"
                                />
                            </IconField>
                        </div>
                    </div>
                </div>
                </template>
            </Card>
        </div>

        <!-- Results Section -->
        <div class="flex flex-col">
            <div class="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-3 lg:mb-4 gap-3 lg:gap-4">
                <div class="flex-1">
                    <h2 class="text-base sm:text-lg lg:text-xl font-semibold text-surface-800">
                        {{ $t('Showing results for your orders:') }} {{ orders.total }} {{ $t('total') }}
                    </h2>
                </div>
            </div>

            <!-- Mobile: Card View -->
            <div class="block lg:hidden">
                <div v-if="!orders.data || orders.data.length === 0" class="text-center py-8">
                    <i class="pi pi-inbox text-4xl text-surface-400 mb-4"></i>
                    <h4 class="text-lg font-medium text-surface-400 mb-2">
                        {{ hasActiveFilters() ? $t('No results with the current filters') : $t('No orders found') }}
                    </h4>
                </div>

                <div v-else class="space-y-2 sm:space-y-3">
                    <Card v-for="order in orders.data" :key="order.id" class="p-3 sm:p-4">
                        <template #content>
                            <div class="flex justify-between items-start mb-2 sm:mb-3">
                                <div class="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                    <div class="min-w-0 flex-1">
                                        <h3 class="text-sm sm:text-base font-medium text-surface-900">#{{ order.number }}</h3>
                                        <div class="flex items-center gap-2 mt-1">
                                            <Tag
                                                :value="getStatusLabel(order.status, $t)"
                                                :severity="getStatusSeverity(order.status)"
                                                size="small"
                                                v-tooltip.top="getCancellationTooltip(order, $t)"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div class="flex items-center gap-1 flex-shrink-0 ml-2">
                                    <Button
                                        icon="pi pi-eye"
                                        text
                                        rounded
                                        size="small"
                                        v-tooltip.top="$t('View Order')"
                                        @click="() => {
                                            try {
                                                const routeName = proxy.route ? proxy.route('client.orders.show', order.id) : `/client/orders/${order.id}`;
                                                router.visit(routeName, {
                                                    onStart: () => showLoading($t('Loading order details...')),
                                                    onFinish: () => hideLoading(),
                                                    onError: () => hideLoading()
                                                });
                                            } catch (e) { console.error(e); }
                                        }"
                                    />
                                    <Button
                                        v-if="order.status === 'COMPLETED' && order.invoice_id"
                                        icon="pi pi-file-pdf"
                                        text
                                        rounded
                                        size="small"
                                        severity="success"
                                        v-tooltip.top="$t('View Invoice')"
                                        @click="() => {
                                            try {
                                                const routeName = proxy.route ? proxy.route('client.orders.invoice', order.id) : `/client/orders/${order.id}/invoice`;
                                                router.visit(routeName, {
                                                    onStart: () => showLoading($t('Loading invoice...')),
                                                    onFinish: () => hideLoading(),
                                                    onError: () => hideLoading()
                                                });
                                            } catch (e) { console.error(e); }
                                        }"
                                    />
                                    <Button
                                        v-if="order.payment_method === 'wise' && order.status === 'WAITING' && wiseBankDetails"
                                        icon="pi pi-send"
                                        text
                                        rounded
                                        size="small"
                                        severity="info"
                                        v-tooltip.top="$t('Wise transfer details')"
                                        @click="openWiseModal(order)"
                                    />
                                    <Button
                                        v-if="order.payment_method === 'pix' && order.status === 'WAITING' && order.mp_payment_details"
                                        icon="pi pi-qrcode"
                                        text
                                        rounded
                                        size="small"
                                        severity="info"
                                        v-tooltip.top="$t('Pix payment details')"
                                        @click="openMpModal(order)"
                                    />
                                    <Button
                                        v-if="order.payment_method === 'boleto' && order.status === 'WAITING' && order.mp_payment_details"
                                        icon="pi pi-barcode"
                                        text
                                        rounded
                                        size="small"
                                        severity="secondary"
                                        v-tooltip.top="$t('Boleto payment details')"
                                        @click="openMpModal(order)"
                                    />
                                </div>
                            </div>
                            <div class="grid grid-cols-2 gap-1 sm:gap-2 text-xs sm:text-sm">
                                <div>
                                    <span class="font-medium text-surface-700">{{ $t('Created at') }}:</span>
                                    <span class="text-surface-600 ml-1">{{ formatDate(order.created_at) }}</span>
                                </div>
                                <div>
                                    <span class="font-medium text-surface-700">{{ $t('Updated at') }}:</span>
                                    <span class="text-surface-600 ml-1">{{ formatDate(order.updated_at) }}</span>
                                </div>
                            </div>
                        </template>
                    </Card>
                </div>

                <!-- Mobile Pagination -->
                <div class="flex justify-between items-center mt-3 sm:mt-4 gap-2">
                    <Button
                        :label="$t('Previous')"
                        icon="pi pi-chevron-left"
                        severity="secondary"
                        size="small"
                        :disabled="!orders.prev_page_url"
                        @click="() => {
                            if (orders.prev_page_url) {
                                router.visit(orders.prev_page_url, {
                                    onStart: () => showLoading($t('Loading previous page...')),
                                    onFinish: () => hideLoading(),
                                    onError: () => hideLoading()
                                });
                            }
                        }"
                    />
                    <span class="text-xs sm:text-sm text-surface-600 text-center px-2">
                        {{ ((orders.current_page || 1) - 1) * (orders.per_page || 10) + 1 }} -
                        {{ Math.min((orders.current_page || 1) * (orders.per_page || 10), orders.total || 0) }}
                        {{ $t('of') }} {{ orders.total || 0 }}
                    </span>
                    <Button
                        :label="$t('Next')"
                        icon="pi pi-chevron-right"
                        iconPos="right"
                        severity="secondary"
                        size="small"
                        :disabled="!orders.next_page_url"
                        @click="() => {
                            if (orders.next_page_url) {
                                router.visit(orders.next_page_url, {
                                    onStart: () => showLoading($t('Loading next page...')),
                                    onFinish: () => hideLoading(),
                                    onError: () => hideLoading()
                                });
                            }
                        }"
                    />
                </div>
            </div>

            <!-- Desktop: Table View -->
            <div class="hidden lg:block">
                <DataTable
                    :value="orders.data"
                    :paginator="false"
                    scrollable
                    scrollHeight="600px"
                    showGridlines
                    class="client-orders-table"
                >
                    <template #empty>
                        <div class="text-center py-8">
                            <i class="pi pi-inbox text-4xl text-surface-400 mb-2"></i>
                            <h4 class="text-lg font-medium text-surface-600 mb-2">
                                {{ hasActiveFilters() ? $t('No results with the current filters') : $t('No orders found') }}
                            </h4>
                        </div>
                    </template>

                    <Column field="number" :header="$t('Number')">
                        <template #body="slotProps">
                            #{{ slotProps.data.number }}
                        </template>
                    </Column>

                    <Column field="status" :header="$t('Status')" style="min-width: 200px">
                        <template #body="slotProps">
                            <Tag
                                :value="getStatusLabel(slotProps.data.status, $t)"
                                :severity="getStatusSeverity(slotProps.data.status)"
                                v-tooltip.top="getCancellationTooltip(slotProps.data, $t)"
                            />
                        </template>
                    </Column>

                    <Column field="created_at" :header="$t('Created at')">
                        <template #body="slotProps">
                            {{ new Date(slotProps.data.created_at).toLocaleString() }}
                        </template>
                    </Column>

                    <Column field="updated_at" :header="$t('Updated at')">
                        <template #body="slotProps">
                            {{ new Date(slotProps.data.updated_at).toLocaleString() }}
                        </template>
                    </Column>

                    <Column :header="$t('Actions')" style="width: 12rem">
                        <template #body="slotProps">
                            <div class="flex gap-2">
                                <Button
                                    icon="pi pi-eye"
                                    text
                                    rounded
                                    v-tooltip.top="$t('View Order')"
                                    @click="() => {
                                        try {
                                            const routeName = proxy.route ? proxy.route('client.orders.show', slotProps.data.id) : `/client/orders/${slotProps.data.id}`;
                                            router.visit(routeName, {
                                                onStart: () => showLoading($t('Loading order details...')),
                                                onFinish: () => hideLoading(),
                                                onError: () => hideLoading()
                                            });
                                        } catch (e) { console.error(e); }
                                    }"
                                />
                                <Button
                                    v-if="slotProps.data.invoice_id"
                                    icon="pi pi-file-pdf"
                                    text
                                    rounded
                                    severity="success"
                                    v-tooltip.top="$t('View Invoice')"
                                    @click="() => {
                                        try {
                                            const routeName = proxy.route ? proxy.route('client.orders.invoice', slotProps.data.id) : `/client/orders/${slotProps.data.id}/invoice`;
                                            router.visit(routeName, {
                                                onStart: () => showLoading($t('Loading invoice...')),
                                                onFinish: () => hideLoading(),
                                                onError: () => hideLoading()
                                            });
                                        } catch (e) { console.error(e); }
                                    }"
                                />
                                <Button
                                    v-if="slotProps.data.payment_method === 'wise' && slotProps.data.status === 'WAITING' && wiseBankDetails"
                                    icon="pi pi-send"
                                    text
                                    rounded
                                    severity="info"
                                    v-tooltip.top="$t('Wise transfer details')"
                                    @click="openWiseModal(slotProps.data)"
                                />
                                <Button
                                    v-if="slotProps.data.payment_method === 'pix' && slotProps.data.status === 'WAITING' && slotProps.data.mp_payment_details"
                                    icon="pi pi-qrcode"
                                    text
                                    rounded
                                    severity="info"
                                    v-tooltip.top="$t('Pix payment details')"
                                    @click="openMpModal(slotProps.data)"
                                />
                                <Button
                                    v-if="slotProps.data.payment_method === 'boleto' && slotProps.data.status === 'WAITING' && slotProps.data.mp_payment_details"
                                    icon="pi pi-barcode"
                                    text
                                    rounded
                                    severity="secondary"
                                    v-tooltip.top="$t('Boleto payment details')"
                                    @click="openMpModal(slotProps.data)"
                                />
                            </div>
                        </template>
                    </Column>

                    <template #footer>
                        <Paginator
                            v-if="orders?.per_page"
                            :rows="orders.per_page"
                            :totalRecords="orders.total || 0"
                            :first="((orders.current_page || 1) - 1) * (orders.per_page || 10)"
                            @page="(event) => {
                                try {
                                    const link = orders.links?.[event.page + 1];
                                    if (link?.url) {
                                        router.visit(link.url, {
                                            onStart: () => showLoading($t('Loading page...')),
                                            onFinish: () => hideLoading(),
                                            onError: () => hideLoading()
                                        });
                                    }
                                } catch (e) { console.error(e); }
                            }"
                        />
                    </template>
                </DataTable>
            </div>
        </div>
    </div>
    <!-- Wise Transfer Details Modal -->
    <Dialog
        v-model:visible="showWiseModal"
        modal
        :header="$t('Wise transfer details')"
        :style="{ width: '560px' }"
        :closable="true"
    >
        <WiseTransferInstructions
            v-if="selectedWiseOrder && wiseBankDetails"
            :order-number="selectedWiseOrder.number"
            :bank-details="{ ...wiseBankDetails, reference: selectedWiseOrder.number }"
            :total="selectedWiseOrder.total_amount"
            :currency="selectedWiseOrder.order_items?.[0]?.sale_coin ?? 'USD'"
            :order-id="selectedWiseOrder.id"
        />
    </Dialog>

    <!-- Mercado Pago Payment Details Modal -->
    <Dialog
        v-model:visible="showMpModal"
        modal
        :header="selectedMpOrder?.payment_method === 'pix' ? $t('Pix payment details') : $t('Boleto payment details')"
        :style="{ width: '600px' }"
        :closable="true"
    >
        <PixInstructions
            v-if="selectedMpOrder?.payment_method === 'pix' && selectedMpOrder?.mp_payment_details"
            :order-number="selectedMpOrder.number"
            :qr-code-base64="selectedMpOrder.mp_payment_details.qr_code_base64"
            :qr-code-text="selectedMpOrder.mp_payment_details.qr_code_text"
            :expiration-date="selectedMpOrder.mp_payment_details.expiration_date"
            :total="selectedMpOrder.total_amount"
            currency="BRL"
            :order-id="selectedMpOrder.id"
        />
        <BoletoInstructions
            v-if="selectedMpOrder?.payment_method === 'boleto' && selectedMpOrder?.mp_payment_details"
            :order-number="selectedMpOrder.number"
            :boleto-url="selectedMpOrder.mp_payment_details.boleto_url"
            :barcode="selectedMpOrder.mp_payment_details.barcode"
            :expiration-date="selectedMpOrder.mp_payment_details.expiration_date"
            :total="selectedMpOrder.total_amount"
            currency="BRL"
            :order-id="selectedMpOrder.id"
        />
    </Dialog>
</template>
<style scoped>

/* Client orders table: header titles must always stay on a single line */
.client-orders-table :deep(.p-datatable-column-title) {
    white-space: nowrap;
}

.client-orders-table :deep(.p-datatable-header-cell) {
    white-space: nowrap;
}

:deep(.p-card-body) {
    padding: 1rem !important;
}
</style>
